// --- Webhook Subscription Models ---

export enum WebhookType {
  Tournaments = 1,
  Courses = 2,
  Players = 3,
  Pairings = 4,
  Scorecards = 5,
  ScorecardDetails = 6,
  Leaderboards = 7,
  Playoffs = 8,
}

export const WebhookTypeLabels: Record<WebhookType, string> = {
  [WebhookType.Tournaments]: 'Tournaments',
  [WebhookType.Courses]: 'Courses',
  [WebhookType.Players]: 'Players',
  [WebhookType.Pairings]: 'Pairings',
  [WebhookType.Scorecards]: 'Scorecards',
  [WebhookType.ScorecardDetails]: 'Scorecard Details',
  [WebhookType.Leaderboards]: 'Leaderboards',
  [WebhookType.Playoffs]: 'Playoffs',
};

/** Human-readable description of what each webhook type delivers. */
export const WebhookTypeDescriptions: Record<WebhookType, string> = {
  [WebhookType.Tournaments]: 'Tournament setup data (XML feed from legacy scoring service)',
  [WebhookType.Courses]: 'Course setup data including holes and yardages',
  [WebhookType.Players]: 'Player biographical and roster data',
  [WebhookType.Pairings]: 'Tee-time pairings and group assignments',
  [WebhookType.Scorecards]: 'Scorecard summary (scores by hole)',
  [WebhookType.ScorecardDetails]: 'Detailed scorecard with shot-level data',
  [WebhookType.Leaderboards]: 'Live leaderboard standings',
  [WebhookType.Playoffs]: 'Playoff bracket and results',
};

/** Integration events that trigger each webhook type. */
export const WebhookTypeTriggerEvents: Record<WebhookType, string[]> = {
  [WebhookType.Tournaments]: [
    'Tournament Created',
    'Tournament Updated',
    'Round Created',
    'Round Updated',
  ],
  [WebhookType.Courses]: [
    'Course Created',
    'Course Updated',
  ],
  [WebhookType.Players]: [
    'Player Created',
    'Player Updated',
  ],
  [WebhookType.Pairings]: [
    'Pairings Published',
  ],
  [WebhookType.Scorecards]: [
    'Scorecard Created',
    'Scorecard Updated',
    'Scorecard Marked Verified',
    'Scorecard Marked Official',
  ],
  [WebhookType.ScorecardDetails]: [
    'Scorecard Created',
    'Scorecard Updated',
    'Scorecard Marked Verified',
    'Scorecard Marked Official',
  ],
  [WebhookType.Leaderboards]: [
    'Leaderboard Updated (coming soon)',
  ],
  [WebhookType.Playoffs]: [
    'Playoff Created',
    'Playoff Updated',
  ],
};

export interface WebhookSubscription {
  id: string;
  type: WebhookType;
  endpointUrl: string;
  clientName: string;
  isActive: boolean;
  createdOnUtc: string;
  modifiedOnUtc: string | null;
}

export interface CreateWebhookSubscriptionRequest {
  clientName: string;
  endpointUrl: string;
  secretKey: string;
  webhookType: WebhookType;
}

export interface UpdateWebhookSubscriptionRequest {
  clientName: string;
  endpointUrl: string;
  secretKey: string;
}

export interface TestWebhookResult {
  success: boolean;
  statusCode: number | null;
  responseBody: string | null;
  errorMessage: string | null;
  elapsedMs: number;
}

// --- Webhook Type Configuration Models ---

export interface WebhookTypeConfiguration {
  type: WebhookType;
  typeName: string;
  isEnabled: boolean;
  description: string;
}
